import { sameCompanyFaq } from "@/lib/brand";
import { createFileRoute } from "@tanstack/react-router";
import { hourlyMinimumFaq } from "@/lib/booking-minimums";
import SeoLandingPage, {
  buildFaqSchema,
  buildLocalBusinessSchema,
} from "@/components/SeoLandingPage";
import heroImage from "@/assets/jet-style-sprinter-white-interior-captain-seats.jpg?format=webp&quality=72&w=1400&imagetools";
import midImage from "@/assets/sprinter-exec-green.jpg?format=webp&quality=72&w=1400&imagetools";
import galleryA from "@/assets/sprinter-executive-2.jpg?format=webp&quality=72&w=1400&imagetools";
import galleryB from "@/assets/sprinter-limo-2.jpg?format=webp&quality=72&w=1400&imagetools";
import galleryC from "@/assets/luxury-suburban-suv-captain-seats-atlanta.jpg?format=webp&quality=72&w=1400&imagetools";

const PAGE_URL = "https://atlantaluxurysprinters.com/luxury-car-service-rates-atlanta";
const PAGE_TITLE = "Luxury Car Service Rates in Atlanta | Pricing & Custom Quotes";
const PAGE_DESC =
  "What luxury car service costs in Atlanta: hourly rates from $250, custom quotes on request, flat airport and FBO transfers, and exactly what is included. Written quotes only. Call (678) 431-7222.";

const faqs = [
  {
    q: "What does luxury car service cost in Atlanta?",
    a: "Chauffeured service with us begins at $250 per hour for a Mercedes-Benz Sprinter, Suburban SUVs are quoted upon request, and airport, FBO, and long-distance work quoted as fixed door-to-door rates. Every figure is issued in writing before you reserve and does not change afterward.",
  },
  {
    q: "How do I get an exact price?",
    a: "Custom quotes are given upon request, based on your date, hours and route. Airport and private-terminal transfers are priced flat rather than hourly.",
  },
  hourlyMinimumFaq({ transfers: true }),
  sameCompanyFaq(),
  {
    q: "Are gratuity and fees included?",
    a: "Gratuity, tolls, parking, and airport access fees are itemized separately and shown plainly on the quote. We do not bury a 'fuel surcharge' or an unexplained 'service fee' in the total, if it is on your invoice, it was on your quote.",
  },
  {
    q: "What happens if my plans run long?",
    a: "Time beyond your reserved block bills in increments at your confirmed hourly rate. The chauffeur stays; nobody is asked to leave a dinner because the block ended. You are told the overage before it accrues, not after.",
  },
  {
    q: "Do you price differently for corporate accounts?",
    a: "Yes. Recurring corporate and family accounts are priced as relationships, one point of contact, standing preferences on file, consolidated monthly billing, and priority access to dates that are otherwise released first-come.",
  },
  {
    q: "Can you match a lower quote from another Atlanta operator?",
    a: "No, and we would rather say so directly. Our rate reflects vehicle age, insurance, chauffeur retention, and the fact that we keep capacity in reserve so your reservation is never the one that gets reassigned. Clients who choose us are not choosing the lowest number in the city.",
  },
];

export const Route = createFileRoute("/luxury-car-service-rates-atlanta")({
  head: () => ({
    meta: [
      { title: PAGE_TITLE },
      { name: "description", content: PAGE_DESC },
      {
        name: "keywords",
        content:
          "luxury car service rates atlanta, atlanta limo prices, black car service cost atlanta, chauffeur service pricing atlanta, executive car service rates atlanta",
      },
      { property: "og:title", content: PAGE_TITLE },
      { property: "og:description", content: PAGE_DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: PAGE_URL },
      { property: "og:image", content: heroImage },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: PAGE_TITLE },
      { name: "twitter:description", content: PAGE_DESC },
      { name: "twitter:image", content: heroImage },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          buildLocalBusinessSchema({
            url: PAGE_URL,
            name: "Atlanta Luxury Sprinters, Luxury Car Service Rates",
            image: heroImage,
            description: PAGE_DESC,
            serviceType: "Luxury Chauffeured Car Service",
            areaServed: [
              { type: "City", name: "Atlanta" },
              { type: "City", name: "Buckhead" },
              { type: "City", name: "Sandy Springs" },
              { type: "Airport", name: "DeKalb-Peachtree Airport (PDK)" },
            ],
          }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <SeoLandingPage
      eyebrow="Pricing • Written Quotes • No Surprises"
      h1="Luxury Car Service Rates in Atlanta"
      intro="You should know what your travel will cost before you book. Sprinters start at $250/hour; airport, private-terminal and long-distance transfers are quoted flat. Text me your plans for a written quote."
      heroImage={heroImage}
      heroAlt="Executive Mercedes-Benz Sprinter cabin used for luxury car service in Atlanta"
      midImage={midImage}
      midAlt="Facing captain's chairs inside an Atlanta executive Sprinter"
      sectionOneTitle="A Clear Quote Before You Book"
      sectionOneBody={[
        "I'll send your quote in writing before you reserve. If you change the itinerary, we'll review any pricing changes together.",
        "Mercedes-Benz Sprinters start at $250/hour. Suburban SUV pricing is custom, for private executive travel, family trips and airport arrivals. Airport, FBO and out-of-area transfers are quoted door to door.",
        "We own and operate our own vehicles, with experienced chauffeurs who have been with my family for years. Text me your date and I'll check availability personally.",
      ]}
      bulletsTitle="Included in Every Rate"
      bulletsIntro="The details below are included in your rate."
      bullets={[
        "Exclusive use of the vehicle for the full reserved block",
        "Vetted, uniformed, background-checked chauffeur",
        "Fuel, detailing, staging, and positioning time",
        "Flight tracking and wait time on airport and FBO arrivals",
        "Chilled water, stemware, and cabin set to your preferences",
        "Unlimited stops within your hours",
        "24-hour support answered by a person",
        "Full commercial insurance and licensed livery authority",
      ]}
      coverageTitle="Rate Coverage Across Metro Atlanta"
      coverageIntro="Standard pricing applies throughout the core market; anything beyond is quoted flat."
      coverage={[
        "Buckhead",
        "Midtown & Downtown",
        "Sandy Springs",
        "Dunwoody & Perimeter",
        "Alpharetta & Milton",
        "Roswell & Johns Creek",
        "Brookhaven",
        "Vinings & Smyrna",
        "Decatur & Druid Hills",
        "Hartsfield-Jackson (ATL)",
        "PDK, FTY & RYY",
        "Lake Oconee & North Georgia",
      ]}
      routeTable={{
        title: "Rate Structure at a Glance",
        intro:
          "Planning ranges for budgeting. Your written quote is exact and accounts for date, duration, and vehicle.",
        rows: [
          {
            route: "Suburban SUV, hourly",
            distance: "Up to 7 guests",
            time: "Custom quote on request",
            notes: "Discreet arrivals, executive pairs, small families.",
          },
          {
            route: "Executive Sprinter, hourly",
            distance: "Up to 8 guests",
            time: "From $250 / hr",
            notes: "Facing captain's chairs, worktable, conversation-quiet cabin.",
          },
          {
            route: "Sprinter Limousine, hourly",
            distance: "Up to 14 guests",
            time: "From $250 / hr",
            notes: "Starlight ceiling, lounge seating, wet bar. Premium on weekends.",
          },
          {
            route: "ATL transfer, flat",
            distance: "In-town",
            time: "Quoted door to door",
            notes: "Meet-and-greet, flight tracking, and reasonable wait included.",
          },
          {
            route: "PDK / FTY / RYY FBO transfer, flat",
            distance: "Metro",
            time: "Quoted door to door",
            notes: "Planeside where permitted; chauffeur staged before wheels down.",
          },
          {
            route: "Evening as-directed",
            distance: "Typical block",
            time: "4–6 hours",
            notes: "Vehicle waits between every stop for the whole evening.",
          },
          {
            route: "Full-day charter",
            distance: "Typical block",
            time: "8–12 hours",
            notes: "One chauffeur all day. Best effective hourly figure.",
          },
          {
            route: "Out-of-market, flat",
            distance: "Savannah, Chattanooga, Lake Oconee",
            time: "Quoted per route",
            notes: "Includes the chauffeur's waiting time and return positioning.",
          },
        ],
        footnote:
          "Gratuity, tolls, parking, and airport access fees are itemized separately. Overtime bills in increments at your confirmed rate, disclosed before it accrues.",
      }}
      detailSections={[
        {
          title: "How Custom Quotes Work",
          body: [
            "An hourly reservation takes the vehicle and the chauffeur off the board for the evening. We do not stack two parties into one night to make a cheap hour work, which is exactly why your 11:40 p.m. departure is not rushed to serve someone else's 12:15.",
            "Custom quotes are given upon request for every hourly booking. Airport and private-terminal transfers are priced as flat door-to-door movements.",
          ],
        },
        {
          title: "The Comparison Clients Actually Make",
          list: [
            "A $150 hour where the vehicle in the photograph is not the vehicle that arrives.",
            "An app quote that reprices itself the moment a stadium empties.",
            "A phone line that stops answering after 9 p.m.",
            "A chauffeur meeting your CEO for the first time on their second week of employment.",
            "Or one written number, one vehicle, one chauffeur, and an evening you never think about again.",
          ],
        },
        {
          title: "Accounts and Standing Arrangements",
          body: [
            "Households and companies that use us regularly move to an account: preferences on file, one point of contact, consolidated monthly billing, and first call on dates before they are released generally.",
            "This is where the rate earns itself back. Nobody re-explains the gate code, the child seat, the preferred entrance, or the fact that the principal does not want conversation before 8 a.m.",
          ],
        },
      ]}
      gallery={{
        title: "The Fleet These Rates Reserve",
        intro: "Three cabins, each priced against what it costs to keep it faultless.",
        images: [
          { src: galleryA, alt: "Executive Sprinter interior for Atlanta luxury car service", caption: "Executive Sprinter, up to 13" },
          { src: galleryB, alt: "Mercedes-Benz Sprinter Limousine interior in Atlanta", caption: "Sprinter Limousine, up to 14" },
          { src: galleryC, alt: "Suburban SUV interior for Atlanta executive transfers", caption: "Suburban SUV, up to 6 (captain seats) or 7 (bench seats)" },
        ],
      }}
      faqs={faqs}
      faqTitle="Atlanta Luxury Car Service Pricing FAQs"
      relatedTitle="Related Pricing Pages"
      related={[
        { to: "/sprinter-limo-cost-atlanta", title: "Sprinter Limo Cost", desc: "What a Sprinter limousine costs in Atlanta, in detail." },
        { to: "/private-chauffeur-hourly-rates-atlanta", title: "Hourly Chauffeur Rates", desc: "As-directed blocks, overtime, and standing accounts." },
        { to: "/corporate-transportation-atlanta", title: "Corporate Transportation", desc: "Accounts with one contact and monthly billing." },
      ]}
      ctaTitle="Text Me for Your Rate"
      ctaBody="Send me your date, pickup, passenger count and itinerary. I'll check availability personally and send your custom quote in writing."
    />
  );
}
